import React from 'react'
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    withPersonalInfo,
    withCustomRouter,
    withTranslation,
    withOrganisation,
    withMsgInfo,
    withAutoMobileInfo,
    withMeasurementUnit,
    withAutoBrand, 
    withEnergySource, 
    withMeansOfTransport, 
    withMotorColors, 
  } from 'components/hoc';
import {
    Select,
    Input,
    Form,
    InputDatePicker,
    ButtonLink,
  } from 'components/common/FormItems';


  const Wrapper = styled.div``;
  const currentYear = new Date().getFullYear();

  const DualSelectWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
      min-width: 200px;
      flex: 1;
      margin-right: 10px;
  
      :last-child {
        margin-right: 0px;
      }
    }
  `;

  
const DualInputWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
    min-width: 150px;
    flex: 1;
    margin-right: 1rem;

    :last-child {
        margin-right: 0px;
    }
    }
    `;

  const StyledMeasurementSelect = styled(Select)`
    padding-right: 2.1rem;
  `;

  const StyledSelect = styled(Select)`
    padding-right: 2rem;
  `;
  
  const StyledInput = styled(Input)`
    padding-right: 1.55rem;
  `;
  
  const StyledDualInput = styled(Input)`
    padding-right: 1.9rem;
  `;

  const StyledInputDatePicker = styled(InputDatePicker)`
    padding-right: 2rem;
  `;

  const StyledButtonLink = styled(ButtonLink)`
    span {
      text-transform: uppercase;
    }
  `;
  
  const AutoMobileFormSchema = {
    means_of_transport_category: {
      validation: 'required',
    },
    auto_brand: {
      validation: 'required',
    },
    construction_year: {
      validation: `integer|digits:4|min:1000|max:${currentYear}`,
    },
  };

const AutoMobileForm = (props) => {
    const {
        tr, 
        basePath,
        routeParams,
        updateAutoMobile,
        addAutoMobile,
        getAutoMobileById,
        setMsgInfo,
        meansOfTransportLocaleOptions,
        autoBrandLocaleOptions,
        energySourceLocaleOptions,
        motorColorsLocaleOptions,
      } = props;

    const [formData, setFormData] = React.useState({});
    const [formSubmitting, setFormSubmitting] = React.useState(false);
    const [formLoading, setFormLoading] = React.useState(false);

    const { id } = routeParams;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fetchData = async () => {
        if (id) {
        try {
            setFormLoading(true);
            const data = await getAutoMobileById(id);
            setFormData({
              ...data,
              construction_year:
                data.construction_year == null
                  ? ''
                  : String(data.construction_year),
            });
            setFormLoading(false);
        } catch (e) {
            setFormData({});
            setFormLoading(false);
        }
        }
    };

  
    React.useEffect(() => {
      fetchData();
      return () => null;
    }, []);

    const onChange = (e) => {
        if (formSubmitting) return;
        const { value, name } = e.target;
        setFormData({ ...formData, [name]: value });
      };

    const onConstructionYearChange = (e) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
      onChange({ target: { name: e.target.name, value } });
    };

    const onSubmit = async (e) => {
        try {
        let response;
        const constructionYear = formData.construction_year;
        const payload = {
          ...formData,
          construction_year:
            constructionYear === '' || constructionYear == null
              ? null
              : Number(constructionYear),
        };

        setFormSubmitting(true);
        if (id) {
            response = await updateAutoMobile(id, payload);
            dispatch(
            setMsgInfo({
                success: true,
                msg: [`${tr('Auto Mobile updated successfully')}`],
            }),
            );
        } else {
            response = await addAutoMobile(payload);
            dispatch(
            setMsgInfo({
                success: true,
                msg: [`${tr('Auto Mobile added successfully')}`],
            }),
            );
        }
        setFormSubmitting(false);

        navigate(basePath);
        window.scrollTo(0, 0);
    
        if (response?.response?.status >= 400) return;
        return;
        } catch (e) {
        setFormSubmitting(false);
        return;
        }
    };

    return (
        <Wrapper className="form-wrapper">
        <div className="flex">
          <StyledButtonLink
            to={basePath}
            className="option-btn"
            icon="angle double left"
            name="All Entries"
          />
        </div>
        <div className="row">
          <div
            className="col-7 ml-10 mt-30 container"
            style={{
              position: 'relative',
              background: 'var(--admincat-color-grey-1)',
            }}
          >
            <br />
    
              <Form
                onSubmit={onSubmit}
                formData={formData}
                isLoading={formSubmitting}
                loadingData={formLoading}
                validationSchema={AutoMobileFormSchema}
                title="Auto Mobile"
                editButtonMarginRight="3.8rem"
                editButtonGap="1rem"
              >
                <StyledSelect
                  optionDefaultValue={tr('Means of Transport Category')}
                  label={tr('Means of Transport Category')}
                  value={formData?.means_of_transport_category || ''}
                  name="means_of_transport_category"
                  onChange={onChange}
                  localeOptions={meansOfTransportLocaleOptions}
                  deselectable
                  sort
                  showRequiredAsterisk
                />

                <StyledSelect
                    optionDefaultValue={tr('Choose Brand')}
                    label={tr('Brand')}
                    value={formData?.auto_brand}
                    name="auto_brand"
                    onChange={onChange}
                    localeOptions={autoBrandLocaleOptions}
                    deselectable
                    showRequiredAsterisk
                    sort
                />

                <DualInputWrapper>
                    <Input
                        className="mr-md-1"
                        value={formData.model_series || ''}
                        onChange={onChange}
                        name="model_series"
                        id="model_series"
                        placeholder="Model Series"
                        label="Model Series"
                    />

                    <StyledDualInput
                        className="ml-md-1"
                        value={formData.type_model_spec || ''}
                        onChange={onChange}
                        name="type_model_spec"
                        id="model_series_type"
                        placeholder="Enter model series type"
                        label="Type (model spec.)"
                    />
                </DualInputWrapper>

                <DualSelectWrapper>
                    <Input
                      label={tr('Construction year')}
                      value={formData.construction_year || ''}
                      name="construction_year"
                      id="construction_year"
                      onChange={onConstructionYearChange}
                      placeholder={tr('Enter a four-digit year')}
                      inputMode="numeric"
                      pattern="[0-9]{4}"
                      maxLength={4}
                      validationErrorMessage={`${tr('Enter a four-digit year between 1000 and')} ${currentYear}.`}
                      tinyInfo={
                        <span>
                          {tr('Four digits')}. {tr('e.g.')} {currentYear}
                        </span>
                      }
                    />
                    <StyledSelect
                        optionDefaultValue={tr('Choose Color')}
                        label={tr('Color')}
                        value={formData?.color}
                        name="color"
                        onChange={onChange}
                        localeOptions={motorColorsLocaleOptions}
                        deselectable
                        sort
                    />
                </DualSelectWrapper> 

                <StyledSelect
                    optionDefaultValue={tr('Choose Energy Source')}
                    label={tr('Fuel or Energy Source')}
                    value={formData?.energy_source || ''}
                    name="energy_source"
                    onChange={onChange}
                    localeOptions={energySourceLocaleOptions}
                    deselectable
                    sort={true}
                />

                <DualSelectWrapper>
                    <Input
                        className="mr-md-1"
                        value={formData.max_speed || ''}
                        onChange={onChange}
                        name="max_speed"
                        id="max_speed"
                        placeholder="Enter Maximum Speed"
                        label="Maximum Speed"
                    />
                    <StyledMeasurementSelect
                        className="ml-md-1"
                        optionDefaultValue={tr('Choose Measurement Unit')}
                        label={tr('Measurement Unit')}
                        value={formData?.measurement_unit}
                        name="measurement_unit"
                        onChange={onChange}
                        localeOptions={props.speedMeasurementLocaleOptions}
                        sort
                        deselectable
                    />
                </DualSelectWrapper> 

                <DualInputWrapper>
                    <Input
                        className="mr-md-1"
                        value={formData.power || ''}
                        onChange={onChange}
                        name="power"
                        id="power"
                        placeholder="Rated Power"
                        label="Rated Power in KW"
                    />

                    <StyledDualInput
                        className="ml-md-1"
                        value={formData.cubic_capacity || ''}
                        onChange={onChange}
                        name="cubic_capacity"
                        id="cubic_capacity"
                        placeholder="Cubic Capacity"
                        label="Cubic Capacity in cm³"
                    />
                </DualInputWrapper>

                <DualInputWrapper>
                    <Input
                        className="mr-md-1"
                        value={formData.no_of_doors || ''}
                        onChange={onChange}
                        name="no_of_doors"
                        id="no_of_doors"
                        placeholder="Enter number of doors"
                        label="Number of doors"
                    />

                    <StyledDualInput
                        className="ml-md-1"
                        value={formData.no_of_seats || ''}
                        onChange={onChange}
                        name="no_of_seats"
                        id="no_of_seats"
                        placeholder="Enter number of seats"
                        label="Number of seats"
                    />
                </DualInputWrapper>
                <StyledInput
                    value={formData.vehicle_id_no || ''}
                    onChange={onChange}
                    name="vehicle_id_no"
                    id="vehicle_id_no"
                    placeholder="Vehicle Identification Number"
                    label="Vehicle Identification Number"
                />

                <StyledInputDatePicker
                    label="Initial Registration Date"
                    value={formData?.initial_reg_date || ''}
                    name="initial_reg_date"
                    onChange={onChange}
              />
            </Form>
            </div>
          </div>
        </Wrapper>
      )
}

export default compose(
    withPersonalInfo,
    withCustomRouter,
    withTranslation,
    withAutoMobileInfo,
    withOrganisation,
    withMsgInfo,
    withAutoBrand, 
    withEnergySource, 
    withMeansOfTransport, 
    withMotorColors, 
    withMeasurementUnit,
  )(AutoMobileForm);
